/**
 * Customer Portal Dashboard Transformers
 * Pure functions to transform API DTOs to domain types
 *
 * Uses types from source domains (finance, orders) to ensure consistency
 */

import type { DashboardData, ChartData, LiveFeedData, RankingsData, FactoryDetailData, EntityDetailData } from '../domain/types'
import type { DashboardDataDto, ChartDataDto, LiveFeedDataDto, RankingsDataDto, FactoryDetailDataDto, EntityDetailDataDto } from './api-types'

/**
 * Transform Dashboard API response to domain type
 * Maps API response to domain types from source domains
 */
export function transformDashboardDtoToDomain(dto: DashboardDataDto): DashboardData {
  return {
    // DashboardBalance extends BalanceSummary from @ufc/domains/finance
    balance: {
      creditLimit: dto.balance.creditLimit,
      usedBalance: dto.balance.usedBalance,
      allowance: dto.balance.allowance,
      availableBalance: dto.balance.availableBalance,
      walletBalance: dto.balance.walletBalance,
      accountStatus: dto.balance.accountStatus,
      paymentTerms: dto.balance.paymentTerms ?? 'net_30',
      vibanNumber: dto.balance.vibanNumber ?? undefined,
      currency: dto.balance.currency ?? 'SAR',
    },
    orders: {
      total: dto.orders.total,
      pending: dto.orders.pending,
      processing: dto.orders.processing,
      delivered: dto.orders.delivered,
    },
    deliveries: {
      totalTons: dto.deliveries.totalTons,
      deliveryCount: dto.deliveries.deliveryCount,
    },
    financial: {
      totalReceivable: {
        amount: dto.financial.totalReceivable.amount,
        currency: dto.financial.totalReceivable.currency,
      },
      overdueInvoices: dto.financial.overdueInvoices,
      pendingPayments: dto.financial.pendingPayments,
    },
    summary: {
      totalQuantityTons: dto.summary.totalQuantityTons,
      citiesCount: dto.summary.citiesCount,
      customersCount: dto.summary.customersCount,
    },
    chartData: dto.chartData ? transformChartDtoToDomain(dto.chartData) : undefined,
  }
}

/**
 * Transform Chart API response to domain type
 */
export function transformChartDtoToDomain(dto: ChartDataDto): ChartData {
  return {
    timeSeries: dto.timeSeries.map(point => ({
      date: point.date,
      label: point.label,
      orders: point.orders,
      tons: point.tons,
    })),
    period: dto.period,
  }
}

/**
 * Transform Rankings API response to domain type
 */
export function transformRankingsDtoToDomain(dto: RankingsDataDto): RankingsData {
  return {
    topProducts: dto.topProducts.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    topProductsByPackaging: dto.topProductsByPackaging.map((item) => ({
      id: item.id,
      name: item.name,
      totalTons: item.totalTons,
      bulkTons: item.bulkTons,
      bagsTons: item.bagsTons,
      deliveries: item.deliveries,
    })),
    topCustomers: dto.topCustomers.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    topCities: dto.topCities.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    topFactories: (dto.topFactories ?? []).map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      bulkTons: item.bulkTons,
      bagsTons: item.bagsTons,
      orderCount: item.orderCount,
      productCount: item.productCount,
      deliveries: item.deliveries,
    })),
    summary: dto.summary ? {
      totalQuantityTons: dto.summary.totalQuantityTons,
      citiesCount: dto.summary.citiesCount,
      customersCount: dto.summary.customersCount,
    } : undefined,
    lastUpdated: dto.lastUpdated,
  }
}

/**
 * Transform Factory Detail API response to domain type
 */
export function transformFactoryDetailDtoToDomain(dto: FactoryDetailDataDto): FactoryDetailData {
  return {
    summary: {
      totalTons: dto.summary.totalTons,
      totalOrders: dto.summary.totalOrders,
      totalProducts: dto.summary.totalProducts,
      totalDeliveries: dto.summary.totalDeliveries,
      bulkTons: dto.summary.bulkTons,
      bagsTons: dto.summary.bagsTons,
    },
    topProducts: dto.topProducts.map((item) => ({
      id: item.id,
      name: item.name,
      totalTons: item.totalTons,
      bulkTons: item.bulkTons,
      bagsTons: item.bagsTons,
      deliveries: item.deliveries,
    })),
    topCustomers: dto.topCustomers.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    topCities: dto.topCities.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    lastUpdated: dto.lastUpdated,
  }
}

/**
 * Transform Entity Detail API response to domain type
 */
export function transformEntityDetailDtoToDomain(dto: EntityDetailDataDto): EntityDetailData {
  return {
    summary: {
      totalTons: dto.summary.totalTons,
      totalOrders: dto.summary.totalOrders,
      totalProducts: dto.summary.totalProducts,
      totalDeliveries: dto.summary.totalDeliveries,
      bulkTons: dto.summary.bulkTons,
      bagsTons: dto.summary.bagsTons,
    },
    topProducts: dto.topProducts.map((item) => ({
      id: item.id,
      name: item.name,
      totalTons: item.totalTons,
      bulkTons: item.bulkTons,
      bagsTons: item.bagsTons,
      deliveries: item.deliveries,
    })),
    topFactories: dto.topFactories.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    topCustomers: dto.topCustomers.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    topCities: dto.topCities.map((item) => ({
      id: item.id,
      name: item.name,
      tons: item.tons,
      deliveries: item.deliveries,
    })),
    lastUpdated: dto.lastUpdated,
  }
}

/**
 * Transform Live Feed API response to domain type
 */
export function transformLiveFeedDtoToDomain(dto: LiveFeedDataDto): LiveFeedData {
  return {
    items: dto.items.map(item => ({
      id: item.id,
      orderNumber: item.orderNumber,
      shipmentNumber: item.shipmentNumber,
      driverName: item.driverName,
      vehiclePlate: item.vehiclePlate,
      factory: item.factory,
      customerName: item.customerName,
      loadingLocation: item.loadingLocation,
      product: item.product,
      plannedQuantity: item.plannedQuantity,
      actualWeight: item.actualWeight,
      status: item.status,
      arrivedAt: item.arrivedAt,
      lastUpdate: item.lastUpdate,
    })),
    totalAtFactory: dto.totalAtFactory,
    lastUpdated: dto.lastUpdated,
  }
}
